import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FileText, MessageSquare, Users, LogOut, Upload, Trash2, RefreshCw, X, Image as ImageIcon, Download } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { COMPANY, SOCIAL_VISUALS } from "@/data/content";

const STATUSES = ["nouveau", "en cours", "traité"];
const statusColor = {
  nouveau: "bg-sky-100 text-sky-700",
  "en cours": "bg-amber-100 text-amber-700",
  "traité": "bg-emerald-100 text-emerald-700",
};

export default function Admin() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("quotes");
  const [stats, setStats] = useState({});
  const [quotes, setQuotes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [clients, setClients] = useState([]);
  const [detail, setDetail] = useState(null);
  const fileRef = useRef();

  useEffect(() => {
    if (user === false) nav("/login");
  }, [user, nav]);

  const load = async () => {
    try {
      const [s, q, c, cl] = await Promise.all([
        api.get("/stats"), api.get("/quotes"), api.get("/contacts"), api.get("/clients"),
      ]);
      setStats(s.data); setQuotes(q.data); setContacts(c.data); setClients(cl.data);
    } catch { /* handled by interceptor */ }
  };

  useEffect(() => { if (user) load(); }, [user]); // eslint-disable-line

  const setQuoteStatus = async (id, status) => {
    await api.patch(`/quotes/${id}`, { status });
    setQuotes((p) => p.map((x) => (x.id === id ? { ...x, status } : x)));
    load();
  };
  const setContactStatus = async (id, status) => {
    await api.patch(`/contacts/${id}`, { status });
    setContacts((p) => p.map((x) => (x.id === id ? { ...x, status } : x)));
    load();
  };
  const delQuote = async (id) => { await api.delete(`/quotes/${id}`); setQuotes((p) => p.filter((x) => x.id !== id)); load(); };
  const delContact = async (id) => { await api.delete(`/contacts/${id}`); setContacts((p) => p.filter((x) => x.id !== id)); load(); };
  const delClient = async (id) => { await api.delete(`/clients/${id}`); setClients((p) => p.filter((x) => x.id !== id)); load(); };

  const importCsv = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/clients/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.inserted} client(s) importé(s)`);
      load();
    } catch {
      toast.error("Import échoué. Vérifiez le format CSV.");
    }
    e.target.value = "";
  };

  const downloadImage = async (url, filename) => {
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objUrl);
    } catch {
      window.open(url, "_blank");
    }
  };

  if (!user) return <div className="min-h-screen flex items-center justify-center text-slate-400">Chargement...</div>;

  const fmtDate = (s) => new Date(s).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });

  const tabs = [
    { id: "quotes", label: "Devis", icon: FileText, count: stats.quotes_new },
    { id: "contacts", label: "Messages", icon: MessageSquare, count: stats.contacts_new },
    { id: "clients", label: "Clients", icon: Users },
    { id: "visuels", label: "Visuels", icon: ImageIcon },
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="admin-page">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-slate-200 hidden md:flex flex-col">
        <div className="p-4 bg-[#0ea5e9]">
          <span className="inline-flex bg-white rounded-lg px-2.5 py-1.5">
            <img src={COMPANY.logo} alt="Equinoxe Propreté" className="h-9 w-auto" />
          </span>
        </div>
        <nav className="p-4 space-y-1 flex-1">
          {tabs.map((t) => (
            <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`w-full flex items-center justify-between px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${tab === t.id ? "bg-sky-50 text-[#0284c7]" : "text-slate-600 hover:bg-slate-50"}`}>
              <span className="flex items-center gap-3"><t.icon size={18} /> {t.label}</span>
              {t.count > 0 && <span className="bg-[#0ea5e9] text-white text-xs px-2 py-0.5 rounded-full">{t.count}</span>}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-200">
          <button data-testid="logout-btn" onClick={() => { logout(); nav("/login"); }} className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50">
            <LogOut size={18} /> Déconnexion
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col">
        <header className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold text-slate-900">Tableau de bord</h1>
            <p className="text-xs text-slate-500">{user.email}</p>
          </div>
          <button onClick={load} className="flex items-center gap-2 text-sm text-slate-600 hover:text-[#0ea5e9]"><RefreshCw size={16} /> Actualiser</button>
        </header>

        <div className="p-6 space-y-6 flex-1">
          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              ["Devis", stats.quotes ?? 0, `${stats.quotes_new ?? 0} nouveaux`],
              ["Messages", stats.contacts ?? 0, `${stats.contacts_new ?? 0} nouveaux`],
              ["Clients", stats.clients ?? 0, "base importée"],
              ["Total demandes", (stats.quotes ?? 0) + (stats.contacts ?? 0), "cumulé"],
            ].map(([l, v, sub]) => (
              <div key={l} className="bg-white rounded-xl border border-slate-200 p-5">
                <p className="text-sm text-slate-500">{l}</p>
                <p className="mt-1 text-3xl font-bold text-slate-900">{v}</p>
                <p className="text-xs text-[#0ea5e9] mt-1">{sub}</p>
              </div>
            ))}
          </div>

          {/* Mobile tabs */}
          <div className="md:hidden flex gap-2">
            {tabs.map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)} className={`flex-1 py-2 rounded-lg text-sm font-medium ${tab === t.id ? "bg-[#0ea5e9] text-white" : "bg-white border border-slate-200 text-slate-600"}`}>{t.label}</button>
            ))}
          </div>

          {/* Content */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            {tab === "quotes" && (
              <Table
                empty="Aucune demande de devis pour le moment."
                headers={["Date", "Nom", "Service", "Type", "Contact", "Statut", ""]}
                rows={quotes.map((q) => [
                  fmtDate(q.created_at), q.name, q.service, q.client_type,
                  <button className="text-[#0ea5e9] hover:underline" onClick={() => setDetail({ ...q, _kind: "Devis" })}>Voir</button>,
                  <StatusSelect value={q.status} onChange={(v) => setQuoteStatus(q.id, v)} testid={`quote-status-${q.id}`} />,
                  <button data-testid={`del-quote-${q.id}`} onClick={() => delQuote(q.id)} className="text-slate-400 hover:text-red-500"><Trash2 size={16} /></button>,
                ])}
              />
            )}
            {tab === "contacts" && (
              <Table
                empty="Aucun message pour le moment."
                headers={["Date", "Nom", "Email", "Source", "Message", "Statut", ""]}
                rows={contacts.map((c) => [
                  fmtDate(c.created_at), c.name, c.email,
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${c.source === "gabriel" ? "bg-sky-100 text-sky-700" : "bg-slate-100 text-slate-600"}`}>{c.source === "gabriel" ? "Gabriel" : "Formulaire"}</span>,
                  <button className="text-[#0ea5e9] hover:underline" onClick={() => setDetail({ ...c, _kind: "Message" })}>Voir</button>,
                  <StatusSelect value={c.status} onChange={(v) => setContactStatus(c.id, v)} testid={`contact-status-${c.id}`} />,
                  <button data-testid={`del-contact-${c.id}`} onClick={() => delContact(c.id)} className="text-slate-400 hover:text-red-500"><Trash2 size={16} /></button>,
                ])}
              />
            )}
            {tab === "clients" && (
              <div>
                <div className="flex items-center justify-between p-4 border-b border-slate-200">
                  <p className="text-sm text-slate-600">{clients.length} client(s)</p>
                  <input ref={fileRef} type="file" accept=".csv" onChange={importCsv} className="hidden" data-testid="csv-input" />
                  <button data-testid="import-csv-btn" onClick={() => fileRef.current.click()} className="flex items-center gap-2 bg-[#0ea5e9] hover:bg-[#0284c7] text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors">
                    <Upload size={16} /> Importer CSV
                  </button>
                </div>
                <Table
                  empty="Aucun client. Importez un fichier CSV (colonnes: nom, email, telephone, ville, type, notes)."
                  headers={["Nom", "Email", "Téléphone", "Ville", "Type", ""]}
                  rows={clients.map((c) => [
                    c.name, c.email, c.phone, c.city, c.type,
                    <button onClick={() => delClient(c.id)} className="text-slate-400 hover:text-red-500"><Trash2 size={16} /></button>,
                  ])}
                />
              </div>
            )}
            {tab === "visuels" && (
              <div className="p-6">
                <p className="text-sm text-slate-600 mb-5">Visuels prêts à publier sur vos réseaux sociaux. Cliquez sur « Télécharger » pour enregistrer l'image sur votre appareil.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {SOCIAL_VISUALS.map((v) => (
                    <div key={v.slug} className="border border-slate-200 rounded-xl overflow-hidden bg-slate-50 flex flex-col">
                      <div className="bg-white flex items-center justify-center p-3 min-h-[16rem]">
                        <img src={v.url} alt={v.title} className="max-h-72 w-auto object-contain rounded-lg" />
                      </div>
                      <div className="p-4 border-t border-slate-200">
                        <p className="text-sm font-semibold text-slate-900">{v.title}</p>
                        <p className="text-xs text-slate-500 mb-3">{v.format}</p>
                        <button data-testid={`download-${v.slug}`} onClick={() => downloadImage(v.url, v.filename)} className="w-full flex items-center justify-center gap-2 bg-[#0ea5e9] hover:bg-[#0284c7] text-white py-2 rounded-lg text-sm font-semibold transition-colors">
                          <Download size={16} /> Télécharger
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {detail && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setDetail(null)}>
          <div className="bg-white rounded-2xl max-w-lg w-full p-7" onClick={(e) => e.stopPropagation()} data-testid="detail-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-900">{detail._kind} — {detail.name}</h3>
              <button onClick={() => setDetail(null)} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
            </div>
            <div className="space-y-2 text-sm text-slate-700">
              <p><b>Email :</b> {detail.email}</p>
              {detail.phone && <p><b>Téléphone :</b> {detail.phone}</p>}
              {detail.client_type && <p><b>Type :</b> {detail.client_type}</p>}
              {detail.service && <p><b>Service :</b> {detail.service}</p>}
              {detail.city && <p><b>Ville :</b> {detail.city}</p>}
              {detail.address && <p><b>Adresse :</b> {detail.address}</p>}
              {detail.message && <p><b>Message :</b><br />{detail.message}</p>}
              <p className="text-xs text-slate-400 pt-2">{fmtDate(detail.created_at)}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const StatusSelect = ({ value, onChange, testid }) => (
  <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className={`text-xs font-semibold px-2.5 py-1 rounded-full border-0 cursor-pointer ${statusColor[value] || "bg-slate-100"}`}>
    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
  </select>
);

const Table = ({ headers, rows, empty }) => (
  <div className="overflow-x-auto">
    {rows.length === 0 ? (
      <p className="p-10 text-center text-sm text-slate-400">{empty}</p>
    ) : (
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-400">
            {headers.map((h, i) => <th key={i} className="px-4 py-3 font-semibold">{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
              {r.map((c, j) => <td key={j} className="px-4 py-3 text-slate-700 whitespace-nowrap">{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);
